import React, {useEffect} from 'react';

import {Navigate, useNavigate, useParams} from 'react-router-dom';

import Login from './components/pages/auth/login';
import LoginHelp from './components/pages/auth/loginHelp';
import ForgotPassword from './components/pages/auth/forgotPassword';
import ForgotPasswordConfirmation from './components/pages/auth/forgotPasswordConfirmation';
import PasswordLinkExpired from './components/pages/auth/PasswordLinkExpired';
import ChangePassword from './components/pages/auth/changePassword';
import VerifyEmail from './components/pages/auth/VerifyEmail';
import Browse from './components/pages/browse';
import CoachAI from './components/pages/coach-ai';
import Home from './components/pages/home';
import Profile from './components/pages/profile';
import Playlist from './components/pages/playlist';
import Search from './components/pages/search';
import UserSettings from './components/pages/userSettings';
import Inbox from './components/pages/inbox';
import Unauthorised from './components/pages/errors/Unauthorised';
import StandaloneSkillsProfilePage from './components/pages/profile/StandaloneSkillsProfilePage';

import {DEFAULT_EDIT_PLAYLIST_ROUTES} from 'screens/SinglePlaylistManager';

import {
	COACH_AI_ROUTE,
	MY_TEAMS_ROUTES,
	PEOPLE_ROUTES,
	PLATFORM_ANNOUNCEMENTS_ROUTE,
	PROFILE_ROUTES,
} from 'utils/routes';
import {isHelixOnlyHub} from 'utils/helix';
import config from 'utils/config';

const MyTeam = React.lazy(
	() => import(/* webpackChunkName: "myteam" */ './components/pages/myTeam')
);
const MyTeams = React.lazy(
	() => import(/* webpackChunkName: "myteams" */ './components/pages/myTeams')
);
const MyTeamMember = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteammember" */ './components/pages/myTeam/people/member'
		)
);

const PeopleSkillsPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "peopleskillspage" */ './components/pages/people/skills'
		)
);

const MyPlaylistStats = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/playlists'
		)
);
const UserProgressAgainstPlaylist = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/playlists/userPlaylistProgress'
		)
);
const MyAssetStats = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/assets'
		)
);
const PlaylistEditorPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "playlist-editor" */ './components/pages/playlist/PlaylistEditor'
		)
);

const PlatformAnnouncementsPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "platform-announcements" */ './components/pages/platform-announcements'
		)
);

export const HOME_ROUTE = isHelixOnlyHub ? PROFILE_ROUTES.skills : '/home';

// At some point it would probably make sense to convert our flat
// route config to a more conventional nested structure. Probably
// in conjunction with an update to React Router 6 or React Location.

// This component is a workaround for a limitation in React Router:
// Normally we'd use a regular Navigate component, but that doesn't
// allow mapping URL parameters to a query string _while also_
// attaching location state.
function RedirectAsset() {
	const navigate = useNavigate();
	const {id} = useParams();
	useEffect(() => {
		navigate(
			{pathname: '/home', search: `?asset=${id}`},
			{replace: true, state: {wasRedirected: true}}
		);
	});
	return null;
}

const ProfileComponent = isHelixOnlyHub ? StandaloneSkillsProfilePage : Profile;

export const publicRoutes = [
	{path: '/', element: <Login />},
	{path: '/login', element: <Login />},
	{path: '/login-help', element: <LoginHelp />},
	{path: '/forgot', element: <ForgotPassword />},
	{path: '/password-requested', element: <ForgotPasswordConfirmation />},
	{path: '/password-link-expired', element: <PasswordLinkExpired />},
	{path: '/change-password', element: <ChangePassword />},
	{path: '/unauthorised', element: <Unauthorised />},
	{path: '/verify-email', element: <VerifyEmail />},
];

const privateBaseRoutes = [
	{path: '/home', element: <Home />},
	...(isHelixOnlyHub
		? [
				{
					path: PROFILE_ROUTES.root,
					element: <Navigate to={PROFILE_ROUTES.skills} replace />,
				},
			]
		: []),
	{
		path: isHelixOnlyHub
			? `${PROFILE_ROUTES.skills}/*`
			: `${PROFILE_ROUTES.root}/*`,
		element: <ProfileComponent />,
	},
	{path: '/browse', element: <Browse />},
	{path: '/browse/:topicId', element: <Browse />},
	{path: '/search', element: <Search />},
	{path: '/settings', element: <UserSettings />},
	{path: '/inbox', element: <Inbox />},
	{path: COACH_AI_ROUTE, element: <CoachAI />},
	{
		path: PLATFORM_ANNOUNCEMENTS_ROUTE,
		element: <PlatformAnnouncementsPage />,
	},
	{path: MY_TEAMS_ROUTES.root, element: <MyTeams />},
	{path: MY_TEAMS_ROUTES.teamMember, element: <MyTeamMember />},
	{path: MY_TEAMS_ROUTES.member, element: <MyTeamMember />},
	{path: '/playlists/:playlistId/analytics', element: <MyPlaylistStats />},
	{
		path: '/playlists/:playlistId/analytics/:teamId',
		element: <MyPlaylistStats />,
	},
	{path: '/assets/:assetId/analytics', element: <MyAssetStats />},
	{path: '/assets/:assetId/analytics/:teamId', element: <MyAssetStats />},
	{
		path: '/user/:userId/playlist/:playlistId',
		element: <UserProgressAgainstPlaylist />,
	},
	{path: `${MY_TEAMS_ROUTES.team}/*`, element: <MyTeam />},
	{
		path: DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistDetails,
		element: <PlaylistEditorPage />,
	},
	{
		path: DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistContent,
		element: <PlaylistEditorPage />,
	},
	{
		path: DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistContentPreview,
		element: <PlaylistEditorPage />,
	},
	{
		path: DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistShare,
		element: <PlaylistEditorPage />,
	},
	{
		path: DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistAccess,
		element: <PlaylistEditorPage />,
	},
	{
		path: DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistAnalytics,
		element: <PlaylistEditorPage />,
	},
	{path: '/playlists/:id/*', element: <Playlist />},
	{path: '/playlists/:id/comments/*', element: <Playlist />},
	{path: '/assets/:id', element: <RedirectAsset />},
];

export const privateRoutes = config.helixAnonymiseUsers
	? privateBaseRoutes
	: [
			...privateBaseRoutes,
			{path: `${PEOPLE_ROUTES.skills}/*`, element: <PeopleSkillsPage />},
		];

export const publicRoutePaths = publicRoutes.map(({path}) => path);
